'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import {
  CatDivider,
  CatAccordion,
  CatAccordionPanel,
  CatIconChevronRight
} from 'components/blocks-components';
import { useDrawer } from 'components/pcc-components';
import ProductSpecsMobileDrawer from 'components/product-specs-mobile-drawer';
import ProductCardAttributes from './product-card-attributes';
import cx from 'classnames';
import styles from '../product-comparison-grid.module.scss';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/search/_components/analytics-listener/types';

export interface ProductCardSpecificationsProps {
  manufacturer: string;
  attributes: Attribute[];
  // TODO: Delete unitOfMeasure to have optional parameter (?) as soon as filtering attr based on UOM is complete for PLP
  unitOfMeasure?: string;
  isProductSpecsMobileDrawerEnabled?: boolean;
  productPriceData?: ProductsPriceData;
}
const ProductCardSpecifications = ({
  manufacturer,
  attributes,
  unitOfMeasure,
  isProductSpecsMobileDrawerEnabled = false,
  ...props
}: ProductCardSpecificationsProps) => {
  const t = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);
  const onExpand = useCallback(() => setIsExpanded(true), []);
  const onClose = useCallback(() => setIsExpanded(false), []);
  const { drawerProps, openDrawer, closeDrawer, isDrawerOpen } = useDrawer();

  const accordionPanelProps = {
    isActive: isExpanded,
    className: cx(
      'border-bottom-0',
      styles['product-comparison__card-specifications__accordion-panel']
    ),
    onBlAccordionOpen: onExpand,
    onBlAccordionClose: onClose
  };

  const content = (
    <div className="d-flex flex-column gap-2">
      {!!manufacturer && (
        <p
          className={cx(
            'cat-u-theme-typography-footnote-bold mb-0',
            styles['product-comparison__card-specifications-label']
          )}
        >
          {manufacturer}
        </p>
      )}
      <ProductCardAttributes
        attributes={attributes}
        unitOfMeasure={unitOfMeasure}
      />
    </div>
  );

  return (
    <>
      <div className="position-relative d-md-flex d-none">
        <div
          className={cx(
            'position-absolute p-3 pt-2',
            styles['product-comparison__card-specifications']
          )}
        >
          <div className="mb-3">
            <CatDivider
              className={
                styles['product-comparison__card-specifications-divider']
              }
            />
          </div>
          {content}
        </div>
      </div>
      <div className="d-md-none">
        {!isProductSpecsMobileDrawerEnabled ? (
          <CatAccordion>
            <CatAccordionPanel
              alignToggleRight
              {...accordionPanelProps}
              data-testid="specifications-accordion"
            >
              <div slot="header">
                {t(isExpanded ? 'CLOSE_SPECIFICATIONS' : 'VIEW_SPECIFICATIONS')}
              </div>
              {content}
            </CatAccordionPanel>
          </CatAccordion>
        ) : (
          <>
            <CatDivider
              className={
                styles['product-comparison__card-specifications-divider']
              }
            />
            <button
              className={styles['product-comparison__card-drawer-button']}
              onClick={openDrawer}
              data-testid="specifications-button"
            >
              <span className="cat-u-theme-typography-body-sm mb-0">
                {t('VIEW_SPECIFICATIONS')}
              </span>
              <div slot="after">
                <CatIconChevronRight slot="after" />
              </div>
            </button>
            {isDrawerOpen && (
              <ProductSpecsMobileDrawer
                {...props}
                drawerProps={drawerProps}
                closeDrawer={closeDrawer}
                unitOfMeasure={unitOfMeasure}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ProductCardSpecifications;
